import { LabelButton } from '@teamsnap/snap-ui';
import { TimelineList } from 'components/common/timeline-list';
import {
  InstallmentEditable,
  InstallmentPaymentMethod,
  MAX_TOTAL_INSTALLMENTS,
} from 'components/registration/v2/types';
import { INSTALLMENTS_EDIT_TYPE, InstallmentsEditType } from 'hooks/amplitude';
import * as React from 'react';
import { useRef, useState } from 'react';
import {
  calculateInstallmentsPlusOne,
  InstallmentFieldError,
  isEditableInstallment,
  recalculateInstallmentsAmounts,
} from './installmentHelpers';
import InstallmentRow from './InstallmentRow';
import InstallmentRowReadOnly from './InstallmentRowReadOnly';

export interface CollectBalanceProps {
  newBalance?: number;
  installments?: InstallmentEditable[]; // filtered to only editable installments
  paidBalance?: number; // sum of paid installments
  updateInstallments: (
    installments: InstallmentEditable[],
    updateType?: InstallmentsEditType
  ) => void;
  savedPaymentMethod?: InstallmentPaymentMethod;
  error?: string;
  fieldErrors?: Record<string, InstallmentFieldError>;
}

export const CollectBalance = ({
  newBalance = 0,
  installments,
  paidBalance = 0,
  updateInstallments,
  savedPaymentMethod,
  error,
  fieldErrors = {},
}: CollectBalanceProps) => {
  const [showMore, setShowMore] = useState(false);
  const showAddButton = installments?.length < MAX_TOTAL_INSTALLMENTS;
  const listRef = useRef<HTMLDivElement>(null);
  const addInstallmentRef = useRef<HTMLButtonElement>(null);
  const installmentsLength =
    installments?.filter(isEditableInstallment).length || 0;

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowMore(!entry.isIntersecting);
      },
      { root: null, threshold: 1 }
    );

    if (addInstallmentRef.current) {
      observer.observe(addInstallmentRef.current);
    }

    return () => observer.disconnect();
  }, [installments, addInstallmentRef.current]);

  const scrollToBottom = () => {
    if (addInstallmentRef?.current) {
      addInstallmentRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const onDeleteIndex = (index: number, installment: InstallmentEditable) => {
    const newInstallments = [...installments];
    if (installment.id) {
      // keep existing installment in the list so BE can mark it as canceled
      newInstallments[index] = {
        ...installment,
        amount: 0,
        status: 'canceled',
        scheduledDate: null,
      };
    } else {
      newInstallments.splice(index, 1);
    }
    updateInstallments(
      recalculateInstallmentsAmounts(newBalance, newInstallments),
      INSTALLMENTS_EDIT_TYPE.INSTALLMENT_REMOVED
    );
  };

  const addInstallment = () => {
    const newInstallments = calculateInstallmentsPlusOne(
      newBalance,
      installments
    );
    updateInstallments(
      newInstallments,
      INSTALLMENTS_EDIT_TYPE.INSTALLMENT_ADDED
    );
    scrollToBottom();
  };

  const updateInstallment = (
    index: number,
    installment: InstallmentEditable,
    editType?: InstallmentsEditType
  ) => {
    const newInstallments = [...installments];
    newInstallments[index] = installment;
    updateInstallments(newInstallments, editType);
  };

  return (
    <div className="sui-flex sui-flex-col sui-gap-1 sui-mb-2 sui-pt-2 sui-w-full sui-border-t sui-border-solid sui-border-gray-90">
      <div className="sui-heading-sm">
        How would you like to collect the balance?
      </div>
      {error && (
        <div
          className="sui-text-negative-text sui-caption"
          data-testid="CollectBalance--error"
        >
          {error}
        </div>
      )}
      <div className="sui-relative" ref={listRef}>
        <TimelineList className="sui-pl-2">
          <InstallmentRowReadOnly amount={paidBalance} status="paid" />
          {installments?.map((installment, index) =>
            installment.status === 'canceled' ? null : (
              <InstallmentRow
                installment={installment}
                key={installment._key}
                onDelete={() => onDeleteIndex(index, installment)}
                onUpdateAmount={(amount) =>
                  {
                    const newAmount = parseFloat(amount.toFixed(2));
                    updateInstallment(
                      index,
                      { ...installment, amount: newAmount },
                      INSTALLMENTS_EDIT_TYPE.INSTALLMENT_AMOUNT_CHANGED
                    )
                  }
                }
                onUpdateDate={(scheduledDate) =>
                  updateInstallment(
                    index,
                    { ...installment, scheduledDate: scheduledDate.toString() },
                    INSTALLMENTS_EDIT_TYPE.INSTALLMENT_DATE_CHANGED
                  )
                }
                max={newBalance}
                canDelete={installmentsLength > 1}
                errors={fieldErrors[installment._key]}
                {...{ savedPaymentMethod }}
              />
            )
          )}
        </TimelineList>
        {showAddButton && (
          <LabelButton
            ref={addInstallmentRef}
            labelText="Add Installment"
            variantType="tertiary"
            icon="add"
            className="sui-self-start sui-mb-2"
            data-testid="CollectBalance--add-installment"
            onClick={addInstallment}
          />
        )}
        {showMore && (
          <LabelButton
            labelText="More"
            size="small"
            icon="arrow_downward"
            className="sui-bg-gray-30 sui-px-2 sui-shadow-2 sui-sticky sui-w-fit"
            data-testid="BalanceChange--more-installments"
            style={{
              borderRadius: '1rem',
              bottom: '20px',
              cursor: 'pointer',
              left: '50%',
              transform: 'translateX(-50%)',
              marginInline: 'auto',
            }}
            onClick={scrollToBottom}
          />
        )}
      </div>
    </div>
  );
};
